import React, { useCallback, useContext, useMemo, useState } from 'react';
import { isNumber } from 'lodash';
import moment, { Moment } from 'moment';

import { useTranslation } from '@lib/useTypedTranslation';
import { useWorldRequest } from '../../../../../lib/useWorldRequest';
import { DeviceInfoContext } from '../../index';
import { DeviceWifiSignalLevel, DeviceWifiSignalLevelData, getDeviceWifiSignalStrength } from '../../../../../services/core/deviceWifi';
import { DatePickerButton } from '@components/datePickerButton/datePickerButton';
import { Loading } from '../../../../../components/loading/loading';
import { NoSelectionOverlay } from '../../../../../components/card/noSelectionOverlay';
import './deviceWifi.css';
import { WifiSignalChart } from './wifiSignalChart';
import { DeviceEvent } from '../../../../../services/core/eventsTypes';

export type WifiSignalChartData = {
  dateFormatted: string,
  date: number,
  local: number,
  level?: number,
  '0'?: number,
  '1'?: number,
  '2'?: number,
  '3'?: number,
  '4'?: number,
  disconnected?: number,
  radioOff?: boolean,
  noDataAvailable?: boolean,
  networkEvents?: number,
  events?: DeviceEvent[],
  tooltipDate?: number,
  status?: string,
  original?: DeviceWifiSignalLevelData
};

export const mapDataToChartGetter = (d: DeviceWifiSignalLevelData): WifiSignalChartData => {
  const base = {
    dateFormatted: moment.utc(d.date).format('HH:mm'),
    date: d.date,
    local: d.local || d.date,
    level: 0,
    status: d.status,
    original: d
  };

  if (d.status === 'radioOff') {
    return { ...base, radioOff: true };
  }

  if (d.status === 'disconnected') {
    return { ...base, disconnected: 5.5 };
  }

  if (!isNumber(d.level)) {
    return { ...base, noDataAvailable: true };
  }

  return { ...base, level: d.level, [`${d.level}`]: d.level + 0.5 };
};

export function DeviceWifi() {
  const { id: deviceId, timeZoneOffset } = useContext(DeviceInfoContext);
  const { t } = useTranslation(['deviceWifi', 'translation']);

  const barColours = [
    'rgb(238, 238, 238)',  // disconnected (striped)
    'rgb(0,0,0)',          // level 0 very poor
    'rgb(220,5,12)',       // level 1 poor
    'rgb(241,147,45)',     // level 2 moderate
    'rgb(202,224,171)',    // level 3 good
    'rgb(78,178,101)'      // level 4 very good
  ];

  const today = moment.utc().add(timeZoneOffset, 'minutes').startOf('day');
  const [selectedDate, setSelectedDate] = useState<number>(today.valueOf());

  const dates = useMemo(() => ({
    from: moment.utc(selectedDate).startOf('day').valueOf(),
    to: moment.utc(selectedDate).endOf('day').valueOf()
  }), [selectedDate]);

  function setDate(date: string | Moment) {
    /* istanbul ignore else */
    if (moment.isMoment(date)) {
      setSelectedDate(date.valueOf());
    }
  }

  const fetcher = useCallback(() => {
    return getDeviceWifiSignalStrength({ deviceId, to: dates.to });
  }, [dates.to, deviceId]);

  const { data, loading } = useWorldRequest<DeviceWifiSignalLevel>(fetcher, { initialLoading: true });

  const noDataAvailable = !data?.filter(d => d.status).length;

  const chartData = data?.map(mapDataToChartGetter);

  return (
    <div className="core-device-wifi">
      <div className="wifi-date-picker-row">
        <DatePickerButton
          initialValue={today}
          onChange={setDate}
          value={selectedDate}
          calendarRange={90}
          isActive={!noDataAvailable}
          timeZoneOffset={timeZoneOffset}
        />
      </div>
      <Loading isLoading={loading} transparentOverlay={false} className="wifi-data-loading">
        <div className="wifi-chart-container">
          <NoSelectionOverlay noSelectionText={t('NO_DATA_AVAILABLE', { ns: 'translation' })} show={noDataAvailable} className="wifi-no-data-overlay" />
          <WifiSignalChart
            selectedDate={selectedDate}
            barColours={barColours}
            data={chartData}
            noDataAvailable={noDataAvailable}
            loading={loading}
          />
        </div>
      </Loading>
    </div>
  );
}
